import { InputMask, createMask, MaskedEnum } from 'imask';
import { isNumeric } from '../utils';
export var TWELVE_HOUR_TIME_MASK = '0`0{:}`0`0 `AM';
export var TWELVE_HOUR_TIME_MASK_WITH_SECONDS = '0`0{:}`0`0{:}`0`0 `AM';
export var TWENTY_FOUR_HOUR_TIME_MASK = '0`0{:}`0`0';
export var TWENTY_FOUR_HOUR_TIME_MASK_WITH_SECONDS = '0`0{:}`0`0{:}`0`0';
var TimeInputMask = /** @class */ (function () {
    function TimeInputMask(_element, _options) {
        var _this = this;
        if (_options === void 0) { _options = {}; }
        this._element = _element;
        this._options = _options;
        this._maskOptions = this._createOptions();
        this._mask = new InputMask(this._element, this._maskOptions);
        if (this._options.onChange) {
            this._acceptListener = function () { return _this._onAccept(); };
            this._mask.on('accept', this._acceptListener);
        }
    }
    TimeInputMask.prototype.destroy = function () {
        if (this._acceptListener) {
            this._mask.off('accept', this._acceptListener);
        }
        this._mask.destroy();
    };
    TimeInputMask.prototype.resolve = function (value) {
        var masked = createMask(this._maskOptions);
        return masked.resolve(value);
    };
    TimeInputMask.prototype.update = function () {
        this._mask.updateValue();
    };
    TimeInputMask.prototype._onAccept = function () {
        if (typeof this._options.onChange === 'function') {
            this._options.onChange(this._mask.value);
        }
    };
    TimeInputMask.prototype._createOptions = function () {
        var _this = this;
        return {
            mask: this._getMaskFormat(),
            overwrite: true,
            lazy: !this._options.showMaskFormat,
            prepare: function (value, masked, flags) { return _this._prepare(value, masked, flags, _this._mask); },
            blocks: {
                A: {
                    mask: MaskedEnum,
                    enum: ['a', 'A', 'p', 'P']
                },
                M: {
                    mask: MaskedEnum,
                    enum: ['m', 'M']
                }
            }
        };
    };
    TimeInputMask.prototype._prepare = function (value, masked, flags, maskInstance) {
        if (typeof this._options.prepareCallback === 'function') {
            return this._options.prepareCallback.call(null, value, masked, flags, this._mask);
        }
        return this._prepareDefault(value, masked, flags, maskInstance).toUpperCase();
    };
    TimeInputMask.prototype._prepareDefault = function (value, masked, flags, maskInstance) {
        if (!flags.input || !value.length || !maskInstance) {
            return value;
        }
        var isAllSelected = maskInstance['_selection'] && maskInstance['_selection'].end === 8;
        var currentValue = isAllSelected ? '' : maskInstance.value;
        if (!isNumeric(value)) {
            // Before we ignore this character let's do some checks for common scenarios where the user enters a colon to help with coercion
            if (value === ':') {
                var isFinalHoursChar = maskInstance.cursorPos === 2 && currentValue.length === 1;
                if (isFinalHoursChar) {
                    // The user attempted to press the colon key after entering a single month character so let's pad the value
                    this._setMaskedValueAdjusted(currentValue.padStart(2, '0'), 3);
                    return ':';
                }
                var isFinalMinutesChar = maskInstance.cursorPos === 5 && currentValue.length === 4;
                if (isFinalMinutesChar) {
                    // The user attempted to press the colon key after entering a single day character so let's pad the value
                    var newValue = "" + currentValue.substring(0, 3) + currentValue[3].padStart(2, '0').padStart(2, '0');
                    this._setMaskedValueAdjusted(newValue, 3);
                    return ':';
                }
                if (this._options.showSeconds) {
                    var isFinalSecondsChar = maskInstance.cursorPos === 8 && currentValue.length === 6;
                    if (isFinalSecondsChar) {
                        // The user attempted to press the colon key after entering a single day character so let's pad the value
                        var newValue = "" + currentValue.substring(0, 6) + currentValue[5].padStart(2, '0') + currentValue.slice(8);
                        this._setMaskedValueAdjusted(newValue, 3);
                        return ':';
                    }
                }
            }
            return value;
        }
        var numValue = +value;
        // Attempt to pad a leading zero to the hours segment
        var isFirstHourChar = maskInstance.cursorPos === 1 && numValue > (this._options.use24HourTime ? 2 : 1);
        if (isFirstHourChar) {
            // Replace just the month segment with the padded value and update cursor position
            var newValue = "" + String(numValue).padStart(2, '0') + currentValue.slice(2);
            this._setMaskedValueAdjusted(newValue, 3);
            return ':';
        }
        // Attempt to pad a leading zero to the minutes segment
        var isFirstMinuteChar = (maskInstance.cursorPos === 3 || maskInstance.cursorPos === 4) && numValue > 5;
        if (isFirstMinuteChar) {
            // Replace just the day segment with the padded value and update cursor position
            var newValue = "" + currentValue.substring(0, 3) + String(numValue).padStart(2, '0') + currentValue.slice(5);
            this._setMaskedValueAdjusted(newValue, 3);
            return ':';
        }
        // Attempt to pad a leading zero to the seconds segment
        if (this._options.showSeconds) {
            var isFirstSecondChar = (maskInstance.cursorPos === 6 || maskInstance.cursorPos === 7) && numValue > 5;
            if (isFirstSecondChar) {
                // Replace just the day segment with the padded value and update cursor position
                var newValue = "" + currentValue.substring(0, 6) + String(numValue).padStart(2, '0') + currentValue.slice(8);
                this._setMaskedValueAdjusted(newValue, 3);
                return ':';
            }
        }
        // Attempt to automatically add a colon after the hours or minutes segment is complete and/or move cursor to after colon
        if (!this._options.showMaskFormat && (maskInstance.cursorPos === 2 || (this._options.showSeconds && maskInstance.cursorPos === 5))) {
            var newValue = void 0;
            if (maskInstance.cursorPos === 2) {
                newValue = "" + currentValue.substring(0, 1) + numValue + ":" + currentValue.slice(3);
            }
            else {
                newValue = "" + currentValue.substring(0, 4) + numValue + ":" + currentValue.slice(6);
            }
            this._setMaskedValueAdjusted(newValue, 2);
            return '';
        }
        return value;
    };
    TimeInputMask.prototype._setMaskedValueAdjusted = function (value, position) {
        var _this = this;
        this._mask.unmaskedValue = value;
        window.requestAnimationFrame(function () { return _this._mask.updateCursor(_this._mask.cursorPos + position); });
    };
    TimeInputMask.prototype._getMaskFormat = function () {
        if (this._options.use24HourTime) {
            return this._options.showSeconds ? TWENTY_FOUR_HOUR_TIME_MASK_WITH_SECONDS : TWENTY_FOUR_HOUR_TIME_MASK;
        }
        return this._options.showSeconds ? TWELVE_HOUR_TIME_MASK_WITH_SECONDS : TWELVE_HOUR_TIME_MASK;
    };
    Object.defineProperty(TimeInputMask.prototype, "maskedValue", {
        get: function () {
            return this._mask.value;
        },
        set: function (value) {
            this._mask.value = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TimeInputMask.prototype, "unmaskedValue", {
        get: function () {
            return this._mask.unmaskedValue;
        },
        set: function (value) {
            this._mask.unmaskedValue = value;
        },
        enumerable: true,
        configurable: true
    });
    return TimeInputMask;
}());
export { TimeInputMask };
